/** @jsxImportSource @emotion/react */

import classNames from 'classnames';
import { resetAuthChecking } from 'modules/auth/store';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'store/store';
import { DivProps } from 'typings';
import UDLink from 'ud-ui/components/link';
import UDLogo from 'ud-ui/components/logo';
import UDText from 'ud-ui/components/text';

import AuthComponentsBillboard from '../components/billboard';
import * as S from './styles';

type Props = {
  title: string;
  subtitle?: string;
  action?: {
    title: string;
    linkLabel: string;
    link: string;
  };
  children: React.ReactNode;
  contentClassName?: string;
  formContainerProps?: DivProps;
  showPrivacyPolicy?: boolean;
};

const AuthLayoutsMain = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const resetCode = useCallback(() => {
    dispatch(resetAuthChecking());
  }, [dispatch]);

  return (
    <S.Container>
      <div className="left">
        <div className="left-container">
          <UDLogo to={Pages.SIGN_IN} className="header block" />
          <div className="content">
            <S.FormContainer {...props.formContainerProps}>
              {(props.title || props.subtitle) && (
                <div className="header flex flex-col justify-between" style={{ gap: 10, marginBottom: 30 }}>
                  {props.title && (
                    <UDText type="title-1" className="text-surfaceWhite" style={{ fontFamily: 'SF Pro Text', fontWeight: 300 }}>
                      {props.title}
                    </UDText>
                  )}
                  {props.subtitle && (
                    <UDText
                      type="callout"
                      style={{ fontFamily: 'SF Pro Text', lineHeight: '18px' }}
                      className="subtitle text-surfaceWhite"
                    >
                      {props.subtitle}
                    </UDText>
                  )}
                </div>
              )}
              <div className={classNames(props.contentClassName)}>{props.children}</div>
              {props.action && (
                <div className="mt-auto">
                  <S.AlreadyContainer>
                    <UDText type="subhead" className="text-surfaceWhite">
                      {props.action.title}
                    </UDText>
                    <UDLink to={props.action.link} type="subhead" className="text-accentNaplesYellow" onClick={resetCode}>
                      {props.action.linkLabel}
                    </UDLink>
                  </S.AlreadyContainer>
                </div>
              )}
              {props.showPrivacyPolicy && (
                <UDText type="subtitle-4" isNew={true} className="text-Gray text-center" style={{ marginTop: 32 }}>
                  {t('signUp.agreementPrefix')}
                  <a
                    href="https://imgameapp.ru/offer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link text-new subtitle-4 muted underline"
                  >
                    {t('signUp.agreementOfferLink')}
                  </a>
                  {t('signUp.agreementMiddle')}
                  <a
                    href="https://imgameapp.ru/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link text-new subtitle-4 muted underline"
                  >
                    {t('signUp.agreementPolicyLink')}
                  </a>
                  {t('signUp.agreementSuffix')}
                </UDText>
              )}
            </S.FormContainer>
          </div>
          <div className="footer">
            <a className="link text subhead mr-6" href="https://imgameapp.ru/#organizer" target="_blank" rel="noreferrer">
              О приложении
            </a>
            <a className="link text subhead mr-6" href="https://imgameapp.ru/privacy-policy" target="_blank" rel="noreferrer">
              Политика конфиденциальности
            </a>
            <a className="link text subhead mr-6" href="https://imgameapp.ru/terms-and-conditions" target="_blank" rel="noreferrer">
              Условия использования
            </a>
          </div>
        </div>
      </div>
      <AuthComponentsBillboard className="right" />;
    </S.Container>
  );
};

export default AuthLayoutsMain;
